import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Upload, Loader2, CheckCircle, AlertCircle, Mic, X } from 'lucide-react';
import apiClient from '../utils/apiClient';

const CustomVoiceUpload = ({ onVoiceCreated }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [voiceName, setVoiceName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null);
  const [uploadedS3Key, setUploadedS3Key] = useState(null);  // Store S3 key for cloning
  const [fileDuration, setFileDuration] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const fileInputRef = useRef(null);

  const validateAudioDuration = (file) => {
    return new Promise((resolve, reject) => {
      const audio = new Audio();
      const url = URL.createObjectURL(file);
      
      audio.addEventListener('loadedmetadata', () => {
        const duration = audio.duration;
        URL.revokeObjectURL(url);
        
        if (duration < 40) {
          reject(`Audio is too short (${duration.toFixed(1)}s). Minimum: 40 seconds`);
        } else if (duration > 300) {
          reject(`Audio is too long (${duration.toFixed(1)}s). Maximum: 300 seconds (5 minutes)`);
        } else {
          resolve(duration);
        }
      });
      
      audio.addEventListener('error', () => {
        URL.revokeObjectURL(url);
        reject('Failed to load audio file');
      });
      
      audio.src = url;
    });
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setValidationError(null);
    
    // Validate file type
    if (!file.name.toLowerCase().endsWith('.mp3')) {
      setValidationError('Please select an MP3 file');
      return;
    }

    // Validate file size (500MB max)
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > 500) {
      setValidationError(`File size (${fileSizeMB.toFixed(1)}MB) exceeds maximum of 500MB`);
      return;
    }

    // Validate duration using browser API
    try {
      const duration = await validateAudioDuration(file);
      setFileDuration(duration);
      setSelectedFile(file);
      toast.success(`Audio file validated (${duration.toFixed(1)}s)`);
    } catch (error) {
      setValidationError(error);
      toast.error(error);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await apiClient.post('/voices/upload-sample', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      setUploadedFileUrl(response.data.presigned_url);  // Use presigned URL for preview
      setUploadedS3Key(response.data.s3_key);  // Store S3 key for cloning
      toast.success('Voice sample uploaded successfully!');
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload voice sample');
      setUploading(false);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleCloneVoice = async () => {
    if (!voiceName.trim()) {
      toast.error('Please enter a name for your custom voice');
      return;
    }

    if (!uploadedS3Key) {
      toast.error('Please upload a voice sample first');
      return;
    }

    setCloning(true);

    try {
      const response = await apiClient.post('/voices/clone', {
        voice_name: voiceName,
        sample_file_key: uploadedS3Key,  // Use stored S3 key directly
      });

      toast.success('Custom voice created successfully!');
      
      // Call callback to refresh voices list
      if (onVoiceCreated) {
        onVoiceCreated(response.data.voice);
      }

      // Reset and close
      handleClose();
      
    } catch (error) {
      console.error('Cloning error:', error);
      toast.error(error.response?.data?.detail || 'Failed to clone voice');
    } finally {
      setCloning(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedFile(null);
    setVoiceName('');
    setUploadProgress(0);
    setUploadedFileUrl(null);
    setUploadedS3Key(null);  // Reset S3 key
    setFileDuration(null);
    setValidationError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer.files[0];
    if (file) {
      // Create a fake event to reuse the validation logic
      handleFileSelect({ target: { files: [file] } });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full" onClick={() => setIsOpen(true)}>
          <Mic className="h-4 w-4 mr-2" />
          Add Custom Voice
        </Button>
      </DialogTrigger>
      
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Mic className="h-5 w-5" />
            <span>Upload Custom Voice</span>
          </DialogTitle>
          <DialogDescription>
            Upload an MP3 recording of your voice (40s - 5min) to create a custom AI voice clone
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Voice Name Input */}
          <div className="space-y-2">
            <Label htmlFor="voice-name">Voice Name</Label>
            <Input
              id="voice-name"
              placeholder="e.g., My Brand Voice"
              value={voiceName}
              onChange={(e) => setVoiceName(e.target.value)}
              disabled={uploading || cloning}
            />
          </div>

          {/* File Upload Area */}
          <div className="space-y-2">
            <Label>Voice Sample (MP3)</Label>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                validationError ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {!selectedFile ? (
                <>
                  <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-sm text-gray-600 mb-2">
                    Drag and drop your MP3 file here, or click to browse
                  </p>
                  <p className="text-xs text-gray-500 mb-4">
                    Max size: 500MB | Duration: 40s - 5min
                  </p>
                  <Input
                    ref={fileInputRef}
                    type="file"
                    accept=".mp3,audio/mpeg"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-upload"
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || cloning}
                  >
                    Select File
                  </Button>
                </>
              ) : (
                <div className="space-y-3">
                  <CheckCircle className="h-12 w-12 mx-auto text-green-500" />
                  <div className="text-sm">
                    <p className="font-medium text-gray-900">{selectedFile.name}</p>
                    <p className="text-gray-600">
                      {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                      {fileDuration && ` • ${fileDuration.toFixed(1)}s`}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedFile(null);
                      setFileDuration(null);
                      setValidationError(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    disabled={uploading || cloning}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Remove
                  </Button>
                </div>
              )}
            </div>
            
            {validationError && (
              <div className="flex items-center space-x-2 text-red-600 text-sm">
                <AlertCircle className="h-4 w-4" />
                <span>{validationError}</span>
              </div>
            )}
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Uploading...</span>
                <span className="text-gray-900 font-medium">{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="w-full" />
            </div>
          )}

          {/* Upload Success */}
          {uploadedFileUrl && !cloning && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-sm text-green-800">Voice sample uploaded successfully!</span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            {!uploadedFileUrl ? (
              <>
                <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={uploading || cloning}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={!selectedFile || uploading || cloning || !!validationError}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Sample
                    </>
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={cloning}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCloneVoice}
                  disabled={!voiceName.trim() || cloning}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                >
                  {cloning ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating Voice...
                    </>
                  ) : (
                    <>
                      <Mic className="h-4 w-4 mr-2" />
                      Create Custom Voice
                    </>
                  )}
                </Button>
              </>
            )}
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Tips for best results:</h4>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• Use clear, high-quality audio with minimal background noise</li>
              <li>• Include varied speech patterns and emotions</li>
              <li>• Longer samples (2-5 minutes) produce better voice clones</li>
              <li>• Speak naturally at a consistent volume</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomVoiceUpload;
